import React from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useLang } from "@/context/LangContext";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Languages, LogIn, LogOut, ShieldCheck, GraduationCap, KeyRound, Briefcase } from "lucide-react";
import WorldClock from "@/components/WorldClock";

export default function Header() {
  const { t, toggle, lang } = useLang();
  const { user, isAdmin, logout } = useAuth();
  const navigate = useNavigate();

  return (
    <header data-testid="site-header" className="glass-header sticky top-0 z-50">
      <div data-testid="header-clock-strip" className="hidden md:flex justify-center border-b border-[hsl(var(--border))]/60 py-1.5 bg-[hsl(var(--muted))]/30">
        <WorldClock lang={lang} />
      </div>
      <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
        <Link to="/" data-testid="brand-link" className="flex items-center gap-2 group">
          <span className="inline-block w-2.5 h-2.5 rounded-full bg-[hsl(var(--primary))] group-hover:scale-110 transition-transform" />
          <span className="font-display text-xl tracking-tight font-medium">Dawnville</span>
          <span className="text-xs text-[hsl(var(--muted-foreground))] hidden sm:inline">· English Studio</span>
        </Link>

        <nav className="hidden lg:flex items-center gap-7 text-sm text-[hsl(var(--foreground))]">
          <a href="/#booking" data-testid="nav-booking" className="hover:text-[hsl(var(--primary))] transition-colors">{t.nav.booking}</a>
          <a href="/#schedules" data-testid="nav-schedules" className="hover:text-[hsl(var(--primary))] transition-colors">{t.nav.schedules}</a>
          <a href="/#announcements" data-testid="nav-announcements" className="hover:text-[hsl(var(--primary))] transition-colors">{t.nav.announcements}</a>
          <a href="/#programs" data-testid="nav-programs" className="hover:text-[hsl(var(--primary))] transition-colors">{t.nav.programs}</a>
          <a href="/#contact" data-testid="nav-contact" className="hover:text-[hsl(var(--primary))] transition-colors">{t.nav.contact}</a>
        </nav>

        <div className="flex items-center gap-2">
          <button
            onClick={toggle}
            data-testid="lang-toggle"
            className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border border-[hsl(var(--border))] hover:bg-[hsl(var(--muted))] transition-colors"
            aria-label="Toggle language"
          >
            <Languages className="w-3.5 h-3.5" />
            <span className="font-medium">{lang === "en" ? "中文" : "EN"}</span>
          </button>

          {user ? (
            <>
              {isAdmin && (
                <Button asChild variant="outline" size="sm" data-testid="header-admin-btn" className="hidden sm:inline-flex">
                  <Link to="/admin"><ShieldCheck className="w-4 h-4 mr-1.5" />{t.nav.admin}</Link>
                </Button>
              )}
              <Button asChild variant="outline" size="sm" data-testid="header-portal-btn" className="hidden sm:inline-flex">
                <Link to="/portal"><GraduationCap className="w-4 h-4 mr-1.5" />{t.nav.portal}</Link>
              </Button>
              <Button
                size="sm"
                onClick={async () => { await logout(); navigate("/"); }}
                data-testid="header-logout-btn"
                className="bg-[hsl(var(--foreground))] text-[hsl(var(--background))] hover:bg-[hsl(var(--foreground))]/90"
              >
                <LogOut className="w-4 h-4 mr-1.5" />{t.nav.logout}
              </Button>
            </>
          ) : (
            <>
              <Button asChild size="sm" data-testid="header-staff-login-btn" variant="outline" className="hidden md:inline-flex border-[hsl(var(--secondary))]/40 text-[hsl(var(--secondary))] hover:bg-[hsl(var(--secondary))]/5">
                <Link to="/staff/login"><Briefcase className="w-4 h-4 mr-1.5" />{t.nav.staff_login || "Staff"}</Link>
              </Button>
              <Button asChild size="sm" data-testid="header-admin-login-btn" variant="outline" className="hidden sm:inline-flex border-[hsl(var(--primary))]/40 text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/5">
                <Link to="/login?role=admin"><KeyRound className="w-4 h-4 mr-1.5" />{t.nav.admin_login}</Link>
              </Button>
              <Button asChild size="sm" data-testid="header-login-btn" variant="outline">
                <Link to="/login"><LogIn className="w-4 h-4 mr-1.5" />{t.nav.login}</Link>
              </Button>
            </>
          )}

          <Button asChild size="sm" data-testid="header-book-btn" className="bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90 text-white">
            <a href="/#booking">{t.hero.cta_book}</a>
          </Button>
        </div>
      </div>
    </header>
  );
}
